import { defineStore } from "pinia"
import { computed, ref } from "vue"
import type { ChatItem, ConversationInfo, FeedbackTag } from "@/types/app"

export const useConversationStore = defineStore("conversation", () => {
  // 状态
  const conversationList = ref<ConversationInfo[]>([])
  const currentConversationId = ref<string>("-1")
  const isNewConversation = ref(true)
  const isLoading = ref(false)
  const chatList = ref<ChatItem[]>([])
  const isResponding = ref(false)
  const currentTaskId = ref<string>("")
  // 反馈标签缓存 - 简单的全局缓存
  const feedbackTags = ref<FeedbackTag[]>([])
  const feedbackTagsLoaded = ref(false)

  // 人工客服相关状态
  const isHumanService = ref(false) // 是否处于人工客服模式

  // 计算属性
  const currentConversation = computed(() => {
    return conversationList.value.find((conv) => conv.conversation_id === currentConversationId.value)
  })

  // 设置加载状态
  function setLoading(loading: boolean) {
    isLoading.value = loading
  }

  // 设置响应状态
  function setIsResponding(responding: boolean) {
    isResponding.value = responding
  }

  // 设置当前任务ID
  function setCurrentTaskId(taskId: string) {
    currentTaskId.value = taskId
  }

  // 设置对话列表
  function setConversationList(conversations: ConversationInfo[]) {
    conversationList.value = conversations
  }

  // 设置当前对话ID
  function setCurrentConversationId(conversation_id: string) {
    currentConversationId.value = conversation_id
    isNewConversation.value = conversation_id === "-1"
    saveCurrentConversation()
  }

  // 设置聊天列表
  function setChatList(chats: ChatItem[]) {
    chatList.value = chats
  }

  // 更新聊天列表中的消息
  function updateChatList(id: string, updatedMessage: Partial<ChatItem>) {
    chatList.value = chatList.value.map((item) => (item.id === id ? { ...item, ...updatedMessage } : item))
  }

  // 更新消息ID（用于将placeholderId更新为真实的messageId）
  function updateMessageId(oldId: string, newId: string, updatedMessage: Partial<ChatItem>) {
    chatList.value = chatList.value.map((item) =>
      item.id === oldId ? { ...item, ...updatedMessage, id: newId } : item
    )
  }

  // 更新消息反馈
  function updateMessageFeedback(id: string, feedback: "0" | "1" | null) {
    const index = chatList.value.findIndex((item) => item.id === id)
    if (index !== -1) chatList.value[index].feedback = feedback
  }

  // 更新对话列表中的对话名称
  function updateConversationName(conversation_id: string, name: string) {
    conversationList.value = conversationList.value.map((conv) =>
      conv.conversation_id === conversation_id ? { ...conv, name } : conv
    )
  }

  // 更新对话信息（从新对话更新为真实对话）
  function updateConversation(conversation_id: string, title?: string) {
    if (!conversation_id) return
    console.log("updateConversation", conversation_id, title)
    // 保存旧ID，用于确认是否需要替换临时对话
    const oldId = currentConversationId.value

    // 更新当前对话ID和状态
    currentConversationId.value = conversation_id
    isNewConversation.value = conversation_id === "-1"

    // 查找是否有临时新对话
    const newConvIndex = conversationList.value.findIndex((conv) => conv.conversation_id === "-1")

    if (newConvIndex !== -1 && oldId === "-1") {
      // 将临时对话更新为真实对话
      const updatedList = [...conversationList.value]
      updatedList[newConvIndex] = {
        ...updatedList[newConvIndex],
        conversation_id,
        title: title || "新对话"
      }
      conversationList.value = updatedList
    } else {
      // 如果没有找到临时对话或不是从临时对话切换过来，添加新对话到列表
      // 检查列表中是否已存在该ID的对话
      const existingIndex = conversationList.value.findIndex((conv) => conv.conversation_id === conversation_id)

      if (existingIndex === -1) {
        // 不存在则添加
        const newConv: ConversationInfo = {
          conversation_id,
          app_id: null,
          title: title || "新对话",
          summary: null,
          status: "0",
          created_time: new Date().toISOString(),
          updated_time: new Date().toISOString(),
          message_count: 0
        }
        conversationList.value = [newConv, ...conversationList.value]
      } else if (title && conversationList.value[existingIndex].title !== title) {
        // 存在但名称不同，则更新名称
        conversationList.value = conversationList.value.map((conv) =>
          conv.conversation_id === conversation_id ? { ...conv, title } : conv
        )
      }
    }

    // 保存当前对话ID
    saveCurrentConversation()
  }

  // 创建新对话
  function createNewConversation() {
    if (isResponding.value) return
    currentConversationId.value = "-1"
    isNewConversation.value = true
    chatList.value = []
  }

  // 移除对话
  function removeConversation(conversation_ids: string | string[]) {
    const ids = Array.isArray(conversation_ids) ? conversation_ids : [conversation_ids]
    conversationList.value = conversationList.value.filter((conv) => !ids.includes(conv.conversation_id))
  }

  // 保存当前对话ID到sessionStorage
  function saveCurrentConversation() {
    if (currentConversationId.value && currentConversationId.value !== "-1")
      sessionStorage.setItem("lastConversationId", currentConversationId.value)
  }

  // 从sessionStorage获取上次的对话ID
  function getLastConversationId(): string {
    return sessionStorage.getItem("lastConversationId") || "-1"
  }

  function resetState() {
    isResponding.value = false
    currentTaskId.value = ""
  }

  // 设置反馈标签
  function setFeedbackTags(tags: FeedbackTag[]) {
    feedbackTags.value = tags
    feedbackTagsLoaded.value = true
  }

  // 设置人工客服模式
  function setHumanService(active: boolean) {
    isHumanService.value = active
  }

  // 清除人工客服状态(空闲超时定时器由 sobotService 模块级管理, 此处不再维护)
  function clearHumanService() {
    isHumanService.value = false
  }

  return {
    // 状态
    conversationList,
    currentConversationId,
    isNewConversation,
    isLoading,
    chatList,
    isResponding,
    currentTaskId,
    feedbackTags,
    feedbackTagsLoaded,

    // 人工客服状态
    isHumanService,

    // 计算属性
    currentConversation,

    // 方法
    setLoading,
    setIsResponding,
    setCurrentTaskId,
    setConversationList,
    setCurrentConversationId,
    setChatList,
    updateChatList,
    updateMessageId,
    updateMessageFeedback,
    updateConversationName,
    updateConversation,
    createNewConversation,
    removeConversation,
    saveCurrentConversation,
    getLastConversationId,
    resetState,
    setFeedbackTags,
    setHumanService,
    clearHumanService
  }
})
